import { useState, useEffect, useCallback, useRef } from 'react';
import {
  addPendingSubmission,
  getPendingSubmissions,
  markSubmissionSynced,
  incrementSubmissionRetry,
  deletePendingSubmission,
  getPendingSubmissionsCount,
} from '../services/indexedDBService';
import { updateInterview, createAttachment } from '../apis';

const SYNC_INTERVAL = 30000; // 30 seconds
const MAX_RETRIES = 5;

export const useOfflineSync = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  // Most recent successful sync event. Consumers (e.g., ApplyQnA) react to
  // this to silently promote queued audios into the "saved" list without
  // re-fetching the whole interview.
  const [lastSyncEvent, setLastSyncEvent] = useState(null);

  const syncIntervalRef = useRef(null);
  const isSyncingRef = useRef(false);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getPendingSubmissionsCount();
      setPendingCount(count);
    } catch (err) {
      console.error('Error getting pending count:', err);
    }
  }, []);

  // Process a single submission
  const processSubmission = useCallback(async (submission) => {
    try {
      const {
        payload,
        alias,
        interviewId,
        pendingAudios = [],
        preuploadedAudioAttachments = [],
        audioBlob, // legacy single-blob entries
      } = submission;

      // Audios already uploaded to S3 in a previous attempt (interview save failed
      // afterwards). Don't re-upload — just relink.
      const newlyUploaded = [];

      // Convert legacy single audioBlob into the array shape transparently.
      const toUpload = pendingAudios.length
        ? pendingAudios
        : audioBlob
          ? [{ blob: audioBlob, duration: null }]
          : [];

      for (let i = 0; i < toUpload.length; i++) {
        const audio = toUpload[i];
        const blob = audio.blob;
        if (!blob) continue;

        const timestamp = Date.now();
        const fileName = `interview_audio_${interviewId}_${timestamp}_${i + 1}.webm`;
        const audioFile = new File([blob], fileName, {
          type: blob.type || 'audio/webm',
        });
        const formData = new FormData();
        formData.append('attachments', audioFile);

        const uploadResp = await createAttachment(formData);
        const fileId = uploadResp.attachments_id;

        const attData = {
          name: fileName,
          size: blob.size,
          type: blob.type || 'audio/webm',
          isInterviewRecording: true,
        };
        if (audio.duration != null) attData.duration = audio.duration;

        newlyUploaded.push({
          attachments_file: fileId,
          attachments_data: attData,
        });
      }

      // Merge all audio attachments (preuploaded + newly uploaded) into the payload
      const finalPayload = { ...payload };
      const allAudioAttachments = [...preuploadedAudioAttachments, ...newlyUploaded];
      if (allAudioAttachments.length > 0) {
        const existingAttachments = finalPayload.interviews_data?.attachments || [];
        const merged = [...existingAttachments];
        for (const att of allAudioAttachments) {
          if (!merged.find((a) => a.attachments_file === att.attachments_file)) {
            merged.push(att);
          }
        }
        finalPayload.interviews_data = {
          ...finalPayload.interviews_data,
          attachments: merged,
        };
      }

      await updateInterview(finalPayload);

      // Mark as synced (removes from queue)
      await markSubmissionSynced(submission.id);
      // Caller uses uploadedAudios to swap pending entries to "saved" in the
      // UI without doing a fresh GET of the interview.
      return { success: true, uploadedAudios: newlyUploaded };
    } catch (error) {
      console.error('Error processing submission:', error);

      // Increment retry count
      const updated = await incrementSubmissionRetry(submission.id, MAX_RETRIES);

      return {
        success: false,
        error,
        shouldRetry: updated.status !== 'failed',
      };
    }
  }, []);

  // Sync all pending submissions
  const syncPendingSubmissions = useCallback(async () => {
    if (isSyncingRef.current || !navigator.onLine) {
      return { synced: 0, failed: 0 };
    }

    isSyncingRef.current = true;
    setIsSyncing(true);
    setSyncError(null);

    let synced = 0;
    let failed = 0;

    try {
      const submissions = await getPendingSubmissions();

      for (const submission of submissions) {
        if (!navigator.onLine) {
          break; // Stop if we go offline
        }

        const result = await processSubmission(submission);
        if (result.success) {
          synced++;
          // Emit an event per synced submission. Each setState produces a new
          // object reference so consumers' useEffect dependencies fire even
          // when the same interview/alias syncs more than once in a batch.
          setLastSyncEvent({
            submissionId: submission.id,
            interviewId: submission.interviewId,
            alias: submission.alias,
            uploadedAudios: result.uploadedAudios || [],
            at: Date.now(),
          });
        } else {
          failed++;
        }
      }

      setLastSyncTime(new Date());
      await updatePendingCount();
    } catch (error) {
      console.error('Sync error:', error);
      setSyncError(error.message);
    } finally {
      setIsSyncing(false);
      isSyncingRef.current = false;
    }

    return { synced, failed };
  }, [processSubmission, updatePendingCount]);

  // Add to queue (for offline or failed submissions)
  const addToQueue = useCallback(async (data) => {
    try {
      const submission = await addPendingSubmission(data);
      await updatePendingCount();
      return submission;
    } catch (error) {
      console.error('Error adding to queue:', error);
      throw error;
    }
  }, [updatePendingCount]);

  // Remove from queue
  const removeFromQueue = useCallback(async (id) => {
    try {
      await deletePendingSubmission(id);
      await updatePendingCount();
    } catch (error) {
      console.error('Error removing from queue:', error);
    }
  }, [updatePendingCount]);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!navigator.onLine) {
      return { synced: 0, failed: 0, offline: true };
    }
    return syncPendingSubmissions();
  }, [syncPendingSubmissions]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      syncPendingSubmissions();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial count
    updatePendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingSubmissions, updatePendingCount]);

  // Periodic sync when online
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      syncIntervalRef.current = setInterval(() => {
        syncPendingSubmissions();
      }, SYNC_INTERVAL);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isOnline, pendingCount, syncPendingSubmissions]);

  return {
    // State
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime,
    syncError,
    lastSyncEvent,
    hasPendingSubmissions: pendingCount > 0,
    // Actions
    addToQueue,
    removeFromQueue,
    triggerSync,
    syncPendingSubmissions,
    updatePendingCount,
  };
};
